import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';

export const usePageSwipe = (nextPath, prevPath = null) => {
  const navigate = useNavigate();

  // Call this to trigger an animated page transition
  // path: where to go, direction: which way the current page exits ('up' or 'down')
  const transitionTo = (path, direction = 'up') => {
    const pageEl = document.getElementById('page-content');
    
    // If going 'up', current page slides off the top; if 'down', slides off the bottom
    const yOut = direction === 'up' ? '-100%' : '100%';

    // Kill any in-progress tweens on this element to prevent conflicts
    gsap.killTweensOf(pageEl);

    gsap.to(pageEl, {
      y: yOut,
      opacity: 0,
      duration: 0.55,
      ease: 'power2.inOut',
      onComplete: () => {
        // Pass direction as route state so the entering page knows which direction to come from
        // e.g. if current page exits 'up', next page should enter from the bottom ('up')
        // if current page exits 'down', next page should enter from the top ('down')
        navigate(path, { state: { direction } });
      },
    });
  };

  useEffect(() => {
    let touchStartY = 0;

    // Record where the touch started
    const onTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };

    const onTouchEnd = (e) => {
      // Positive delta = swiped up, negative = swiped down
      const delta = touchStartY - e.changedTouches[0].clientY;

      // Swipe up past threshold → go to next page (exits upward)
      if (delta > 60 && nextPath) transitionTo(nextPath, 'up');
      // Swipe down past threshold → go to previous page (exits downward)
      if (delta < -60 && prevPath) transitionTo(prevPath, 'down');
    };

    const onWheel = (e) => {

      // Scrolling down at the top of the page → next page
      if (e.deltaY > 40 && nextPath) transitionTo(nextPath, 'up');
      // Scrolling up at the bottom of the page → previous page
      if (e.deltaY < -40 && prevPath) transitionTo(prevPath, 'down');
    };

    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('wheel', onWheel, { passive: true });

    // Cleanup listeners when the component using this hook unmounts
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('wheel', onWheel);
    };
  }, [nextPath, prevPath]);

  return { transitionTo };
};